import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { 
  Plus, 
  Trash2, 
  Search, 
  Share,
  Pin,
  Lock,
  List,
  CheckSquare,
  Bold,
  Italic,
  Underline,
  Strikethrough,
  Paperclip
} from 'lucide-react';
import { createEditor, Descendant, Editor, Transforms, Element as SlateElement } from 'slate';
import { Slate, Editable, withReact, ReactEditor, useSlate } from 'slate-react';
import { withHistory } from 'slate-history';

// Define Slate element and leaf types

type ParagraphElement = { type: 'paragraph'; children: Descendant[] };
type BulletedListElement = { type: 'bulleted-list'; children: Descendant[] };
type NumberedListElement = { type: 'numbered-list'; children: Descendant[] };
type ListItemElement = { type: 'list-item'; children: Descendant[] };
type CheckListItemElementType = { type: 'check-list-item'; checked: boolean; children: Descendant[] };
type CustomElement = ParagraphElement | BulletedListElement | NumberedListElement | ListItemElement | CheckListItemElementType;
type CustomText = { text: string; bold?: boolean; italic?: boolean; underline?: boolean; strikethrough?: boolean };
declare module 'slate' {
  interface CustomTypes {
    // Remove direct reference to Editor to avoid self-referencing type error
    Element: CustomElement;
    Text: CustomText;
  }
}

// Move initialValue above its first use
const initialValue: Descendant[] = [
  {
    type: 'paragraph',
    children: [{ text: '' }],
  },
];

interface Note {
  id: string;
  title: string;
  content: string;
  createdAt: Date;
  updatedAt: Date;
  isPinned: boolean;
  isLocked: boolean;
  folder: string;
  tags: string[];
  attachments: string[];
  isDeleted: boolean;
  deletedAt?: Date;
}

interface FolderItem {
  id: string;
  name: string;
  icon: string;
  color: string;
  isExpanded: boolean;
}

export default function App() {
  const [notes, setNotes] = useState<Note[]>([]);
  const [selectedNote, setSelectedNote] = useState<Note | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFolder, setSelectedFolder] = useState<string>('all');
  const [showFormattingToolbar, setShowFormattingToolbar] = useState(false);
  const [formattingState, setFormattingState] = useState({
    bold: false,
    italic: false,
    underline: false,
    strikethrough: false
  });
  const editorRef = useRef<HTMLDivElement>(null);
  const [editorValue, setEditorValue] = useState<Descendant[]>(initialValue);
  const editor = useMemo(() => withHistory(withReact(createEditor())) as ReactEditor, []);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const folders: FolderItem[] = [
    { id: 'all', name: 'All Notes', icon: '📝', color: '#007aff', isExpanded: true },
    { id: 'recent', name: 'Recently Deleted', icon: '🗑️', color: '#ff3b30', isExpanded: false },
    { id: 'personal', name: 'Personal', icon: '👤', color: '#34c759', isExpanded: true },
    { id: 'work', name: 'Work', icon: '💼', color: '#ff9500', isExpanded: true },
    { id: 'ideas', name: 'Ideas', icon: '💡', color: '#af52de', isExpanded: true },
  ];

  // Clear search when switching folders
  const handleFolderSelect = (folderId: string) => {
    setSelectedFolder(folderId);
    setSearchTerm(''); // Clear search when switching folders
  };

  // Load notes from localStorage on component mount
  useEffect(() => {
    const savedNotes = localStorage.getItem('apple-notes');
    if (savedNotes) {
      const parsedNotes = JSON.parse(savedNotes).map((note: any) => ({
        ...note,
        createdAt: new Date(note.createdAt),
        updatedAt: new Date(note.updatedAt),
        isDeleted: note.isDeleted || false,
        deletedAt: note.deletedAt ? new Date(note.deletedAt) : undefined
      }));
      setNotes(parsedNotes);
    } else {
      // Create some sample notes
      const sampleNotes: Note[] = [
        {
          id: '1',
          title: 'Welcome to Notes',
          content: 'This is your first note. Start typing to create your own notes!',
          createdAt: new Date(),
          updatedAt: new Date(),
          isPinned: true,
          isLocked: false,
          folder: 'personal',
          tags: ['welcome'],
          attachments: [],
          isDeleted: false
        },
        {
          id: '2',
          title: 'Shopping List',
          content: '• Milk\n• Bread\n• Eggs\n• Bananas\n• Coffee',
          createdAt: new Date(Date.now() - 86400000),
          updatedAt: new Date(Date.now() - 86400000),
          isPinned: false,
          isLocked: false,
          folder: 'personal',
          tags: ['shopping'],
          attachments: [],
          isDeleted: false
        }
      ];
      setNotes(sampleNotes);
    }
  }, []);

  // Save notes to localStorage whenever notes change
  useEffect(() => {
    localStorage.setItem('apple-notes', JSON.stringify(notes));
  }, [notes]);

  const createNewNote = useCallback(() => {
    const newNote: Note = {
      id: Date.now().toString(),
      title: 'Untitled Note',
      content: '',
      createdAt: new Date(),
      updatedAt: new Date(),
      isPinned: false,
      isLocked: false,
      folder: selectedFolder === 'all' ? 'personal' : selectedFolder,
      tags: [],
      attachments: [],
      isDeleted: false
    };
    setNotes([newNote, ...notes]);
    setSelectedNote(newNote);
  }, [notes, selectedFolder]);

  const updateNote = (id: string, updates: Partial<Note>) => {
    const updatedNotes = notes.map(note =>
      note.id === id
        ? { ...note, ...updates, updatedAt: new Date() }
        : note
    );
    setNotes(updatedNotes);
    
    if (selectedNote && selectedNote.id === id) {
      setSelectedNote({ ...selectedNote, ...updates, updatedAt: new Date() });
    }
  };

  const deleteNote = (id: string) => {
    const noteToDelete = notes.find(note => note.id === id);
    if (noteToDelete) {
      // Move to trash instead of permanent delete
      const updatedNotes = notes.map(note =>
        note.id === id
          ? { ...note, isDeleted: true, deletedAt: new Date() }
          : note
      );
      setNotes(updatedNotes);
      
      if (selectedNote && selectedNote.id === id) {
        setSelectedNote(null);
      }
    }
  };

  const restoreNote = (id: string) => {
    const updatedNotes = notes.map(note =>
      note.id === id
        ? { ...note, isDeleted: false, deletedAt: undefined }
        : note
    );
    setNotes(updatedNotes);
  };

  const permanentlyDeleteNote = (id: string) => {
    const updatedNotes = notes.filter(note => note.id !== id);
    setNotes(updatedNotes);
    
    if (selectedNote && selectedNote.id === id) {
      setSelectedNote(null);
    }
  };

  const emptyTrash = () => {
    const updatedNotes = notes.filter(note => !note.isDeleted);
    setNotes(updatedNotes);
    setSelectedNote(null);
  };

  const togglePinNote = (id: string) => {
    const note = notes.find(n => n.id === id);
    if (note) {
      updateNote(id, { isPinned: !note.isPinned });
    }
  };

  const toggleLockNote = (id: string) => {
    const note = notes.find(n => n.id === id);
    if (note) {
      updateNote(id, { isLocked: !note.isLocked });
    }
  };

  const filteredNotes = notes.filter(note => {
    const matchesSearch = searchTerm === '' || 
    note.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      note.content.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFolder = selectedFolder === 'all' 
      ? !note.isDeleted 
      : selectedFolder === 'recent' 
        ? note.isDeleted 
        : note.folder === selectedFolder && !note.isDeleted;
    
    return matchesSearch && matchesFolder;
  });

  const pinnedNotes = filteredNotes.filter(note => note.isPinned);
  const unpinnedNotes = filteredNotes.filter(note => !note.isPinned);

  const getNotePreview = (content: string) => {
    const plainText = content.replace(/<[^>]*>/g, '');
    return plainText.length > 60 ? plainText.substring(0, 60) + '...' : plainText;
  };

  const formatDate = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (days === 0) {
      return 'Today';
    } else if (days === 1) {
      return 'Yesterday';
    } else if (days < 7) {
      return `${days} days ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  const handleEditorFocus = () => {
    setShowFormattingToolbar(true);
  };

  const handleEditorBlur = () => {
    setTimeout(() => setShowFormattingToolbar(false), 200);
  };

  // Define Slate element types
  const LIST_TYPES = ['numbered-list', 'bulleted-list'];
  const CHECKLIST_TYPE = 'check-list-item';

  // Helper: convert plain text to Slate value
  function plainTextToSlateValue(text: string): Descendant[] {
    return [{ type: 'paragraph', children: [{ text }] }];
  }

  // On app startup, migrate all notes in localStorage to valid Slate JSON
  useEffect(() => {
    const savedNotes = localStorage.getItem('apple-notes');
    if (savedNotes) {
      let notes = [];
      try {
        notes = JSON.parse(savedNotes);
      } catch {
        notes = [];
      }
      let migrated = false;
      const fixedNotes = notes.map((note: any) => {
        try {
          const parsed = JSON.parse(note.content);
          if (Array.isArray(parsed)) {
            return note;
          } else if (typeof parsed === 'string') {
            migrated = true;
            return { ...note, content: JSON.stringify(plainTextToSlateValue(parsed)) };
          } else {
            migrated = true;
            return { ...note, content: JSON.stringify(plainTextToSlateValue('')) };
          }
        } catch {
          migrated = true;
          return { ...note, content: JSON.stringify(plainTextToSlateValue(note.content || '')) };
        }
      });
      if (migrated) {
        localStorage.setItem('apple-notes', JSON.stringify(fixedNotes));
      }
    }
  }, []);

  // Robust note loading
  useEffect(() => {
    if (selectedNote) {
      try {
        const parsed = JSON.parse(selectedNote.content);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setEditorValue(parsed);
        } else if (typeof parsed === 'string') {
          setEditorValue(plainTextToSlateValue(parsed));
        } else {
          setEditorValue(initialValue);
        }
      } catch {
        // If not JSON, treat as plain text
        setEditorValue(plainTextToSlateValue(selectedNote.content || ''));
      }
    }
  }, [selectedNote]);

  // On note save, always save as JSON.stringify(value)
  const handleSlateChange = (value: Descendant[]) => {
    setEditorValue(value);
    if (selectedNote) {
      updateNote(selectedNote.id, { content: JSON.stringify(value) });
    }
  };

  // Toolbar button helpers
  const toggleMark = (editor: Editor, format: string) => {
    const isActive = isMarkActive(editor, format);
    if (isActive) {
      Editor.removeMark(editor, format);
    } else {
      Editor.addMark(editor, format, true);
    }
  };

  const isMarkActive = (editor: Editor, format: string) => {
    const marks = Editor.marks(editor) as CustomText | null;
    return marks ? marks[format as keyof CustomText] === true : false;
  };

  const toggleBlock = (editor: Editor, format: string) => {
    const isActive = isBlockActive(editor, format);
    const isList = LIST_TYPES.includes(format) || format === CHECKLIST_TYPE;
    Transforms.unwrapNodes(editor, {
      match: n => !Editor.isEditor(n) && SlateElement.isElement(n) && (LIST_TYPES.includes((n as any).type as string) || (n as any).type === CHECKLIST_TYPE),
      split: true,
    });
    const newType = isActive ? 'paragraph' : format;
    Transforms.setNodes(editor, { type: newType } as any);
    if (!isActive && isList) {
      const block = { type: format as CustomElement['type'], children: [] } as CustomElement;
      Transforms.wrapNodes(editor, block);
    }
  };

  const isBlockActive = (editor: Editor, format: string) => {
    // @ts-ignore
    for (const [node] of Editor.nodes(editor, {
      match: n => !Editor.isEditor(n) && SlateElement.isElement(n) && (n as any).type === format,
    })) {
      return true;
    }
    return false;
  };

  // Add renderElement back in scope
  const renderElement = useCallback((props: { attributes: any; children: any; element: CustomElement | any }) => {
    switch (props.element.type) {
      case 'bulleted-list':
        return <ul {...props.attributes}>{props.children}</ul>;
      case 'numbered-list':
        return <ol {...props.attributes}>{props.children}</ol>;
      case 'list-item':
        return <li {...props.attributes}>{props.children}</li>;
      case 'check-list-item':
        return <CheckListItemElement {...(props as { attributes: any; children: any; element: CheckListItemElementType })} />;
      case 'attachment':
        return (
          <div {...props.attributes} style={{ margin: '8px 0' }}>
            <a href={props.element.url} target="_blank" rel="noopener noreferrer" style={{ color: '#007aff', textDecoration: 'underline', fontWeight: 500 }}>
              📎 {props.element.filename}
            </a>
          </div>
        );
      default:
        return <p {...props.attributes}>{props.children}</p>;
    }
  }, []);

  // Move CheckListItemElement above renderElement
  const CheckListItemElement = ({ attributes, children, element }: { attributes: any; children: any; element: CheckListItemElementType }) => {
    const editor = useSlate() as ReactEditor;
    const checked = !!element.checked;
    return (
      <div {...attributes} className="check-list-item">
        <span
          contentEditable={false}
          onClick={() => {
            const path = ReactEditor.findPath(editor, element);
            Transforms.setNodes(editor, { checked: !checked }, { at: path });
          }}
        >
          <input type="checkbox" checked={checked} readOnly />
        </span>
        <span contentEditable suppressContentEditableWarning>{children}</span>
      </div>
    );
  };

  // 1. Fix checklist deletion: handle Backspace at start of checklist item
  const handleKeyDown = (event: React.KeyboardEvent) => {
    const { selection } = editor;
    if (!selection || !ReactEditor.isFocused(editor)) return;
    // @ts-ignore
    for (const [node, path] of Editor.nodes(editor, {
      match: n => !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === 'check-list-item',
    })) {
      if (
        event.key === 'Backspace' &&
        Editor.isStart(editor, selection.anchor, path) &&
        node.children && node.children[0] && Editor.string(editor, path) === ''
      ) {
        event.preventDefault();
        Transforms.setNodes(editor, { type: 'paragraph' }, { at: path });
        return;
      }
    }
  };

  // 2. Fix renderLeaf to properly render bold, italic, underline, strikethrough
  const Leaf = ({ attributes, children, leaf }: { attributes: any; children: any; leaf: CustomText }) => {
    if (leaf.bold) children = <strong>{children}</strong>;
    if (leaf.italic) children = <em>{children}</em>;
    if (leaf.underline) children = <u>{children}</u>;
    if (leaf.strikethrough) children = <s>{children}</s>;
    return <span {...attributes}>{children}</span>;
  };

  // 3. Add Attach button to the toolbar
  const insertAttachment = (editor: Editor, file: File) => {
    const url = URL.createObjectURL(file);
    const text = file.name;
    const attachment = { type: 'paragraph', children: [{ text: '' }] };
    Transforms.insertNodes(editor, [
      {
        type: 'attachment',
        url,
        filename: text,
        children: [{ text: text }],
      } as any,
      attachment,
    ]);
  };

  // Listen for checkbox clicks and update note content
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;
    const handleCheckboxClick = (e: Event) => {
      if ((e.target as HTMLElement).tagName === 'INPUT' && (e.target as HTMLInputElement).type === 'checkbox') {
        // Update note content after checkbox toggle
        setTimeout(() => {
          if (selectedNote && editorRef.current) {
            updateNote(selectedNote.id, { content: editorRef.current.innerHTML });
          }
        }, 0);
      }
    };
    editor.addEventListener('click', handleCheckboxClick);
    return () => {
      editor.removeEventListener('click', handleCheckboxClick);
    };
  }, [selectedNote]);

  // Set editor content when switching notes
  useEffect(() => {
    if (editorRef.current && selectedNote) {
      editorRef.current.innerHTML = selectedNote.content || '';
    }
    // Do NOT set innerHTML in response to every keystroke or state update
  }, [selectedNote]);

  // handleEditorInput only updates state, does not set innerHTML

  // Bullet/checkbox helpers
  // Remove all remaining code that references value, selectionStart, selectionEnd, setSelectionRange, or getCurrentLineInfo for the editorRef (now a contentEditable div)
  // Remove or comment out any bullet/checkbox helpers or logic that tries to manipulate the contentEditable div as if it were a textarea
  // Only keep the execCommand-based formatting, bullet, and checkbox logic
  // The editor should update note content on input and set content on note switch

  // Add Link to selected text
  const addLink = () => {
    if (!editorRef.current || !selectedNote) return;
    const url = window.prompt('Enter URL:', 'https://');
    if (!url) return;
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    const range = selection.getRangeAt(0);
    const selectedText = selection.toString() || 'link';
    const a = document.createElement('a');
    a.href = url;
    a.textContent = selectedText;
    a.target = '_blank';
    range.deleteContents();
    range.insertNode(a);
    // Move cursor after the link
    range.setStartAfter(a);
    range.setEndAfter(a);
    selection.removeAllRanges();
    selection.addRange(range);
    // @ts-ignore
    for (const range of selection.getRanges()) {
      ReactEditor.focus(editor);
    }
    // Update note content
    updateNote(selectedNote.id, { content: editorRef.current.innerHTML });
  };

  // Attach file to note
  const attachFile = () => {
    if (!selectedNote || !editorRef.current) return;
    const input = document.createElement('input');
    input.type = 'file';
    input.onchange = (e: any) => {
      const file = e.target.files[0];
      if (!file) return;
      const updatedAttachments = [...selectedNote.attachments, file.name];
      // Append attachment reference visually
      if (editorRef.current) {
        editorRef.current.innerHTML += `<div>[Attachment: ${file.name}]</div>`;
        updateNote(selectedNote.id, { content: editorRef.current.innerHTML, attachments: updatedAttachments });
      }
    };
    input.click();
  };

  // Auto-continue bullet/checkbox on Enter
  // Remove all remaining code that references value, selectionStart, selectionEnd, setSelectionRange, or getCurrentLineInfo for the editorRef (now a contentEditable div)
  // Remove the handleEditorKeyDown function entirely, as it is no longer compatible
  // Remove the placeholder prop from the contentEditable div, as it is not a valid prop
  // If you want a placeholder, implement it with a conditional overlay div instead
  // Ensure the editor is clean and linter-error free

  // Electron integration
  useEffect(() => {
    if (window.electronAPI) {
      // Listen for new note command from menu
      window.electronAPI.onNewNote(() => {
        createNewNote();
      });

      // Listen for save note command from menu
      window.electronAPI.onSaveNote(() => {
        if (selectedNote) {
          // Auto-save is already handled by localStorage
          console.log('Note saved');
        }
      });

      // Cleanup listeners
      return () => {
        window.electronAPI.removeAllListeners('new-note');
        window.electronAPI.removeAllListeners('save-note');
      };
    }
  }, [selectedNote, createNewNote]);

  // Add effect to close sidebar on desktop resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 600 && sidebarOpen) setSidebarOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [sidebarOpen]);

  // Add a function to close sidebar when overlay is clicked
  const closeSidebar = () => setSidebarOpen(false);

  return (
    <div className="app-root">
      {/* Hamburger menu for mobile */}
      <button
        className="hamburger"
        aria-label="Open sidebar"
        aria-controls="sidebar"
        aria-expanded={sidebarOpen}
        onClick={() => setSidebarOpen(true)}
      >
        <span />
        <span />
        <span />
      </button>
      {/* Sidebar overlay for mobile */}
      <div
        className={`sidebar-overlay${sidebarOpen ? ' open' : ''}`}
        onClick={closeSidebar}
        aria-hidden={!sidebarOpen}
      />
      {/* Sidebar */}
      <nav
        id="sidebar"
        className={`sidebar${sidebarOpen ? ' open' : ''}`}
        aria-hidden={!sidebarOpen && window.innerWidth < 768}
      >
        <div className="sidebar-header">Kemax Note</div>
        <div className="sidebar-section">Folders</div>
        <div className="folders-section">
          {folders.map(folder => (
            <div key={folder.id} className="folder-item">
              <div 
                className={`folder-header ${selectedFolder === folder.id ? 'active' : ''}`}
                onClick={() => handleFolderSelect(folder.id)}
              >
                <span className="folder-icon">{folder.icon}</span>
                <span className="folder-name">{folder.name}</span>
                <span className="folder-count">
                  {folder.id === 'all' 
                    ? notes.filter(n => !n.isDeleted).length 
                    : folder.id === 'recent'
                      ? notes.filter(n => n.isDeleted).length
                      : notes.filter(n => n.folder === folder.id && !n.isDeleted).length}
                </span>
              </div>
            </div>
          ))}
        </div>
        <div className="sidebar-section">Actions</div>
        <div className="notes-section">
          <div className="section-header">
            <span className="section-title">Notes</span>
          </div>
          
          {pinnedNotes.length > 0 && (
            <div className="notes-group">
              <div className="group-header">Pinned</div>
              {pinnedNotes.map(note => (
            <div
              key={note.id}
              className={`note-item ${selectedNote?.id === note.id ? 'active' : ''}`}
              onClick={() => setSelectedNote(note)}
            >
                  <div className="note-header">
              <div className="note-title">{note.title}</div>
                    <div className="note-actions">
                      {note.isLocked && <Lock size={12} className="lock-icon" />}
                      <button 
                        className="note-action-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          togglePinNote(note.id);
                        }}
                        title="Unpin Note"
                      >
                        <Pin size={12} />
                      </button>
                      {selectedFolder === 'recent' && (
                        <button 
                          className="note-action-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            restoreNote(note.id);
                          }}
                          title="Restore Note"
                        >
                          <Plus size={12} />
                        </button>
                      )}
                    </div>
                  </div>
              <div className="note-preview">{getNotePreview(note.content)}</div>
                  <div className="note-meta">
                    <span className="note-date">{formatDate(note.updatedAt)}</span>
                    {note.tags.length > 0 && (
                      <span className="note-tags">
                        {note.tags.slice(0, 2).join(', ')}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="notes-group">
            {pinnedNotes.length > 0 && <div className="group-header">Notes</div>}
            {unpinnedNotes.map(note => (
              <div
                key={note.id}
                className={`note-item ${selectedNote?.id === note.id ? 'active' : ''}`}
                onClick={() => setSelectedNote(note)}
              >
                <div className="note-header">
                  <div className="note-title">{note.title}</div>
                  <div className="note-actions">
                    {note.isLocked && <Lock size={12} className="lock-icon" />}
                    <button 
                      className="note-action-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        togglePinNote(note.id);
                      }}
                      title={note.isPinned ? "Unpin Note" : "Pin Note"}
                    >
                      <Pin size={12} />
                    </button>
                    {selectedFolder === 'recent' && (
                      <>
                        <button 
                          className="note-action-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            restoreNote(note.id);
                          }}
                          title="Restore Note"
                        >
                          <Plus size={12} />
                        </button>
                        <button 
                          className="note-action-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            permanentlyDeleteNote(note.id);
                          }}
                          title="Delete Permanently"
                        >
                          <Trash2 size={12} />
                        </button>
                      </>
                    )}
                  </div>
                </div>
                <div className="note-preview">{getNotePreview(note.content)}</div>
                <div className="note-meta">
                  <span className="note-date">{formatDate(note.updatedAt)}</span>
                  {note.tags.length > 0 && (
                    <span className="note-tags">
                      {note.tags.slice(0, 2).join(', ')}
                    </span>
                  )}
                </div>
            </div>
          ))}
          </div>
        </div>
      </nav>

      {/* Main content */}
      <main className="main-content" style={window.innerWidth <= 600 && sidebarOpen ? { filter: 'blur(2px)' } : {}}>
        {selectedNote ? (
          <>
            <div className="editor-header">
              <div className="editor-title-container">
                <input
                  type="text"
                  value={selectedNote.title}
                  onChange={(e) => updateNote(selectedNote.id, { title: e.target.value })}
                  className="editor-title-input"
                  placeholder="Title"
                />
              </div>
              <div className="editor-actions">
                <button className="action-btn" title="Share">
                  <Share size={16} />
                </button>
                <button 
                  className="action-btn" 
                  title="Pin Note"
                  onClick={() => togglePinNote(selectedNote.id)}
                >
                  <Pin size={16} />
                </button>
                <button 
                  className="action-btn" 
                  title="Lock Note"
                  onClick={() => toggleLockNote(selectedNote.id)}
                >
                  <Lock size={16} />
                </button>
                <button 
                  className="action-btn"
                  title="Delete Note"
                  onClick={() => deleteNote(selectedNote.id)}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>

            <Slate editor={editor} initialValue={editorValue} onChange={handleSlateChange}>
              <div className="formatting-toolbar">
                <div className="toolbar-group">
                  <button className="toolbar-btn" title="Bold" onMouseDown={e => { e.preventDefault(); toggleMark(editor, 'bold'); }}>
                    <Bold size={14} />
                  </button>
                  <button className="toolbar-btn" title="Italic" onMouseDown={e => { e.preventDefault(); toggleMark(editor, 'italic'); }}>
                    <Italic size={14} />
                  </button>
                  <button className="toolbar-btn" title="Underline" onMouseDown={e => { e.preventDefault(); toggleMark(editor, 'underline'); }}>
                    <Underline size={14} />
                  </button>
                  <button className="toolbar-btn" title="Strikethrough" onMouseDown={e => { e.preventDefault(); toggleMark(editor, 'strikethrough'); }}>
                    <Strikethrough size={14} />
                  </button>
                </div>
                <div className="toolbar-group">
                  <button className="toolbar-btn" title="Bullet List" onMouseDown={e => { e.preventDefault(); toggleBlock(editor, 'bulleted-list'); }}>
                    <List size={14} />
                  </button>
                  <button className="toolbar-btn" title="Checklist" onMouseDown={e => { e.preventDefault(); toggleBlock(editor, CHECKLIST_TYPE); }}>
                    <CheckSquare size={14} />
                  </button>
                  <button className="toolbar-btn" title="Attach File" onMouseDown={e => { e.preventDefault(); document.getElementById('file-input')?.click(); }}>
                    <Paperclip size={14} />
                  </button>
                  <input id="file-input" type="file" style={{ display: 'none' }} onChange={e => {
                    if (e.target.files && e.target.files[0]) insertAttachment(editor, e.target.files[0]);
                  }} />
                </div>
              </div>
              <Editable
                renderElement={renderElement}
                renderLeaf={Leaf}
                placeholder="Start typing your note..."
                spellCheck
                autoFocus
                className="editor rich-editor"
                onKeyDown={handleKeyDown}
              />
            </Slate>
          </>
        ) : (
          <div className="empty-state">
            <div className="empty-state-icon">📝</div>
            <div className="empty-state-text">No Note Selected</div>
            <div className="empty-state-subtext">
              Select a note from the sidebar or create a new one
            </div>
            <button className="empty-state-btn" onClick={createNewNote}>
              <Plus size={16} />
              Create New Note
            </button>
          </div>
        )}
      </main>
    </div>
  );
} 